/**
 * Global filter store with Zustand
 * Manages filter state, URL sync, and persistence
 */
import { create } from 'zustand';
import type { FilterState, FilterPreset, TimePreset, TestMode, EventType } from '@/shared/types';
import { storage, STORAGE_KEYS } from '@/shared/utils/storage';

interface FilterStore extends FilterState {
  // Actions
  setPeriod: (period: TimePreset | 'custom') => void;
  setCustomRange: (dateFrom: string, dateTo: string) => void;
  setBrands: (brands: string[]) => void;
  setEventTypes: (eventTypes: EventType[]) => void;
  setCountries: (countries: string[]) => void;
  setSources: (sources: string[]) => void;
  setTestMode: (testMode: TestMode) => void;
  setAffiliateId: (affiliateId: string | undefined) => void;
  setTrackingTag: (trackingTag: string | undefined) => void;
  setTagSender: (tagSender: string | undefined) => void;
  setTagGeo: (tagGeo: string | undefined) => void;
  setTagSheetDate: (tagSheetDate: string | undefined) => void;

  // Preset management
  savePreset: (name: string) => void;
  loadPreset: (presetId: string) => void;
  deletePreset: (presetId: string) => void;
  getPresets: () => FilterPreset[];

  // Utilities
  reset: () => void;
  toURLSearchParams: () => URLSearchParams;
  fromURLSearchParams: (params: URLSearchParams) => void;
}

const DEFAULT_FILTERS: FilterState = {
  period: 'today',
  brands: [],
  eventTypes: [],
  countries: [],
  sources: [],
  testMode: 'exclude',
};

/**
 * Load initial state from localStorage or defaults
 */
const getInitialState = (): FilterState => {
  const saved = storage.get<FilterState>(STORAGE_KEYS.FILTER_STATE);
  return saved || DEFAULT_FILTERS;
};

/**
 * Create filter store
 */
export const useFilterStore = create<FilterStore>((set, get) => ({
  ...getInitialState(),

  setPeriod: (period) => {
    set({ period, dateFrom: undefined, dateTo: undefined });
    persistState(get());
  },

  setCustomRange: (dateFrom, dateTo) => {
    set({ period: 'custom', dateFrom, dateTo });
    persistState(get());
  },

  setBrands: (brands) => {
    set({ brands });
    persistState(get());
  },

  setEventTypes: (eventTypes) => {
    set({ eventTypes });
    persistState(get());
  },

  setCountries: (countries) => {
    set({ countries });
    persistState(get());
  },

  setSources: (sources) => {
    set({ sources });
    persistState(get());
  },

  setTestMode: (testMode) => {
    set({ testMode });
    persistState(get());
  },

  setAffiliateId: (affiliateId) => {
    set({ affiliateId });
    persistState(get());
  },

  setTrackingTag: (trackingTag) => {
    set({ trackingTag });
    persistState(get());
  },

  setTagSender: (tagSender) => {
    set({ tagSender });
    persistState(get());
  },

  setTagGeo: (tagGeo) => {
    set({ tagGeo });
    persistState(get());
  },

  setTagSheetDate: (tagSheetDate) => {
    set({ tagSheetDate });
    persistState(get());
  },

  savePreset: (name) => {
    const state = get();
    const presets = storage.get<FilterPreset[]>(STORAGE_KEYS.FILTER_PRESETS) || [];

    const newPreset: FilterPreset = {
      id: `preset_${Date.now()}`,
      name,
      filters: extractFilterState(state),
      createdAt: new Date().toISOString(),
    };

    storage.set(STORAGE_KEYS.FILTER_PRESETS, [...presets, newPreset]);
  },

  loadPreset: (presetId) => {
    const presets = storage.get<FilterPreset[]>(STORAGE_KEYS.FILTER_PRESETS) || [];
    const preset = presets.find((p) => p.id === presetId);

    if (preset) {
      set(preset.filters);
      persistState(get());
    }
  },

  deletePreset: (presetId) => {
    const presets = storage.get<FilterPreset[]>(STORAGE_KEYS.FILTER_PRESETS) || [];
    storage.set(
      STORAGE_KEYS.FILTER_PRESETS,
      presets.filter((p) => p.id !== presetId),
    );
  },

  getPresets: () => {
    return storage.get<FilterPreset[]>(STORAGE_KEYS.FILTER_PRESETS) || [];
  },

  reset: () => {
    set(DEFAULT_FILTERS);
    persistState(get());
  },

  toURLSearchParams: () => {
    const state = get();
    const params = new URLSearchParams();

    if (state.period !== 'today') {
      params.set('period', state.period);
    }

    if (state.period === 'custom' && state.dateFrom && state.dateTo) {
      params.set('from', state.dateFrom);
      params.set('to', state.dateTo);
    }

    if (state.brands.length > 0) {
      params.set('brands', state.brands.join(','));
    }

    if (state.eventTypes.length > 0) {
      params.set('events', state.eventTypes.join(','));
    }

    if (state.countries.length > 0) {
      params.set('countries', state.countries.join(','));
    }

    if (state.sources.length > 0) {
      params.set('sources', state.sources.join(','));
    }

    if (state.testMode !== 'exclude') {
      params.set('test', state.testMode);
    }

    if (state.affiliateId) {
      params.set('aff', state.affiliateId);
    }

    if (state.trackingTag) {
      params.set('tag', state.trackingTag);
    }

    if (state.tagSender) {
      params.set('tagSender', state.tagSender);
    }

    if (state.tagGeo) {
      params.set('tagGeo', state.tagGeo);
    }

    if (state.tagSheetDate) {
      params.set('tagSheet', state.tagSheetDate);
    }

    return params;
  },

  fromURLSearchParams: (params) => {
    const updates: Partial<FilterState> = {};

    const period = params.get('period');
    if (period) {
      updates.period = period as TimePreset | 'custom';
    }

    const dateFrom = params.get('from');
    const dateTo = params.get('to');
    if (dateFrom && dateTo) {
      updates.dateFrom = dateFrom;
      updates.dateTo = dateTo;
    }

    const brands = params.get('brands');
    if (brands) {
      updates.brands = brands.split(',').filter(Boolean);
    }

    const events = params.get('events');
    if (events) {
      updates.eventTypes = events.split(',').filter(Boolean) as EventType[];
    }

    const countries = params.get('countries');
    if (countries) {
      updates.countries = countries.split(',').filter(Boolean);
    }

    const sources = params.get('sources');
    if (sources) {
      updates.sources = sources.split(',').filter(Boolean);
    }

    const test = params.get('test');
    if (test) {
      updates.testMode = test as TestMode;
    }

    const aff = params.get('aff');
    if (aff) {
      updates.affiliateId = aff;
    }

    const tag = params.get('tag');
    if (tag) {
      updates.trackingTag = tag;
    }

    const tagSender = params.get('tagSender');
    if (tagSender) {
      updates.tagSender = tagSender;
    }

    const tagGeo = params.get('tagGeo');
    if (tagGeo) {
      updates.tagGeo = tagGeo;
    }

    const tagSheet = params.get('tagSheet');
    if (tagSheet) {
      updates.tagSheetDate = tagSheet;
    }

    set(updates);
    persistState(get());
  },
}));

/**
 * Extract filter state (exclude actions)
 */
function extractFilterState(state: FilterStore): FilterState {
  return {
    period: state.period,
    dateFrom: state.dateFrom,
    dateTo: state.dateTo,
    brands: state.brands,
    eventTypes: state.eventTypes,
    countries: state.countries,
    sources: state.sources,
    testMode: state.testMode,
    affiliateId: state.affiliateId,
    trackingTag: state.trackingTag,
    tagSender: state.tagSender,
    tagGeo: state.tagGeo,
    tagSheetDate: state.tagSheetDate,
  };
}

/**
 * Persist state to localStorage
 */
function persistState(state: FilterStore) {
  storage.set(STORAGE_KEYS.FILTER_STATE, extractFilterState(state));
}
